import { add, isEqual } from "date-fns";

import { dateToStringKey } from "utils/dateUtils";

class TimeSlot {
  static fromAPI = (data) => ({
    end: data.end,
    start: data.start,
  });

  /**
   * This method merge timeslots that are overlapping each other to get a clean list of timeslot.
   * @param {[{start: Date, end: Date}]} timeSlots The list of timeslots to merge.
   * @return {[{start: Date, end: Date}]} The list of clean timeslots merged.
   */
  static mergeTimeSlots = (timeSlots) => {
    if (!timeSlots || !timeSlots.length) {
      return [];
    }

    const sortedTimeSlots = timeSlots
      .map((ts) => ({ start: new Date(ts.start), end: new Date(ts.end) }))
      // eslint-disable-next-line no-nested-ternary
      .sort((ts1, ts2) => (ts1.start <= ts2.start ? ts1.start === ts2.start ? 0 : -1 : 1));

    const mergedTimeSlots = [sortedTimeSlots.shift()];

    sortedTimeSlots
      .forEach((ts) => {
        const lastMergedTs = mergedTimeSlots[mergedTimeSlots.length - 1];
        if (ts.start <= lastMergedTs.end) {
          mergedTimeSlots[mergedTimeSlots.length - 1] = {
            start: lastMergedTs.start,
            end: ts.end > lastMergedTs.end ? ts.end : lastMergedTs.end,
          };
        } else {
          mergedTimeSlots.push({
            start: ts.start,
            end: ts.end,
          });
        }
      });

    return mergedTimeSlots;
  };

  /**
   * This method split a timeslot in sub timeslot of a given duration.
   * @param {{start: Date, end: Date}} timeSlot The timeslot to split in sub timeslots.
   * @param {number} durationTimeMinute The duration of the sub timeslots in minutes.
   * @param {boolean} shouldReturnUnexpiredOnly Whether we want to return only the timeslot in the present and future.
   * @return {[{start: Date, end: Date}]} The list of sub timeslots generated.
   */
  static splitTimeSlot = (timeSlot, durationTimeMinute, shouldReturnUnexpiredOnly = false) => {
    const start = new Date(timeSlot.start);
    const end = new Date(timeSlot.end);
    let currentStart = start;
    let currentEnd = start;
    let splitTimeSlots = [];
    while (currentEnd < end) {
      currentEnd = add(currentStart, { minutes: durationTimeMinute });
      splitTimeSlots.push({
        start: currentStart,
        end: currentEnd < end ? currentEnd : end,
      });
      currentStart = currentEnd;
    }

    if (shouldReturnUnexpiredOnly) {
      const now = new Date();
      splitTimeSlots = splitTimeSlots.filter((ts) => ts.start >= now);
    }

    return splitTimeSlots;
  };

  /**
   * This method remove the duplicated timeslots from a list and return the cleaned list.
   * @param {[{start: Date, end: Date}]} timeSlots The list of timeslots to clean.
   * @return {[{start: Date, end: Date}]} The list of timeslots cleaned with no duplicates.
   */
  static removeDuplicatesTimeSlots = (timeSlots) => (
    timeSlots.reduce((accumulator, currentValue) => {
      if (!accumulator.find((obj) => isEqual(obj.start, currentValue.start) && isEqual(obj.end, currentValue.end))) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, [])
  );

  /**
   * This method group the timeslots of a list by day of their start date.
   * So that we can get easily the list of timeslots for each date.
   * @param {[{start: Date, end: Date}]} timeSlots The list of timeslots to sort and group.
   * @return {{Object}} The timeslots grouped by date.
   * The key of the object is an array representing the date and the value is a list of timeslots of starting this date.
   */
  static groupTimeSlotByStartDate = (timeSlots) => {
    const sortedTimeSlots = timeSlots
      .map((ts) => ({ ...ts, start: new Date(ts.start), end: new Date(ts.end) }))
      // eslint-disable-next-line no-nested-ternary
      .sort((ts1, ts2) => (ts1.start <= ts2.start ? ts1.start === ts2.start ? 0 : -1 : 1));

    const timeSlotByStartDay = {};
    sortedTimeSlots
      .forEach((ts) => {
        timeSlotByStartDay[dateToStringKey(ts.start)] = [];
      });
    sortedTimeSlots
      .forEach((ts) => {
        timeSlotByStartDay[dateToStringKey(ts.start)] = [
          ...timeSlotByStartDay[dateToStringKey(ts.start)],
          ts,
        ];
      });

    return timeSlotByStartDay;
  };
}

export default TimeSlot;
